import React, { useState } from 'react';
import InfoModal from './InfoModal';
import METRIC_EXPLANATIONS from './metricExplanations';

export const fmtNum = (v, casas = 0) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  });

export const fmtDate = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

// Mini gráfico de barras — 24h do dia ou a série diária, conforme a métrica.
// Passando o mouse (ou tocando) numa coluna, mostra rótulo + valor.
export function Spark({ values, color, labels, unit, decimals, valueDisplays }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...values.map((v) => v || 0));
  const n = Math.max(1, values.length);
  const W = 100, H = 34, bw = W / n;
  const media = values.length > 0
    ? values.reduce((acc, v) => acc + (v || 0), 0) / values.length
    : 0;
  const mediaY = H - (media / max) * H;

  return (
    <div className="hh-spark-wrap">
      <svg
        className="hh-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
      >
        {values.map((v, i) => {
          const h = ((v || 0) / max) * H;
          return (
            <rect key={`b${i}`} x={i * bw + bw * 0.2} y={H - h}
              width={bw * 0.6} height={h}
              fill={color} opacity={hover === null || hover === i ? 1 : 0.4} />
          );
        })}
        {/* Linha branca com a média dos dias mostrados no gráfico. */}
        <line
          className="hh-spark-avg" x1={0} y1={mediaY} x2={W} y2={mediaY}
        />
        {/* Faixas transparentes de largura cheia: facilitam acertar a coluna. */}
        {values.map((_, i) => (
          <rect key={`h${i}`} x={i * bw} y={0} width={bw} height={H} fill="transparent"
            onMouseEnter={() => setHover(i)} onTouchStart={() => setHover(i)} />
        ))}
      </svg>

      {hover !== null && (
        <div
          className="hh-tip"
          style={{ left: `${((hover + 0.5) / n) * 100}%` }}
        >
          <b>{labels?.[hover] ?? ''}</b> {valueDisplays?.[hover] || `${fmtNum(values[hover], decimals)}${unit || ''}`}
        </div>
      )}
    </div>
  );
}

// Card no mesmo formato dos cards de saúde do dashboard.
export function MetricCard({ m }) {
  const [showInfo, setShowInfo] = useState(false);
  // Seta só aparece quando dá pra dizer se melhorou ou piorou.
  const seta = m.better === true ? '▲' : m.better === false ? '▼' : null;
  const classe = m.better === true ? 'up' : m.better === false ? 'down' : '';
  const explicacao = METRIC_EXPLANATIONS[m.key];
  return (
    <div className="hh-card">
      <div className="hh-card-head">
        <div className="hh-card-head-left">
          <span className="hh-title">{m.label}</span>
          <span className="hh-sub">
            {m.sub_label || (m.chart === 'hourly' ? 'Hoje' : fmtDate(m.reference_date))}
          </span>
        </div>
        <div className="hh-card-head-right">
          <div className="hh-value-row">
            <span className="hh-value" style={{ color: m.color }}>
              {m.value_display || (
                <>
                  {fmtNum(m.value, m.decimals)}
                  {m.unit && <small>{m.unit}</small>}
                </>
              )}
            </span>
            {seta && (
              <span
                className={`hh-arrow ${classe}`}
                title={`Anterior: ${m.previous_display || fmtNum(m.previous, m.decimals)}`}
              >
                {seta}
              </span>
            )}
          </div>
          {m.delta != null && (
            <span className="hh-delta">
              {m.delta_display || `${m.delta > 0 ? '+' : ''}${fmtNum(m.delta, m.decimals)}`} vs. anterior
            </span>
          )}
        </div>
      </div>
      <div
        className="hh-spark-clickarea"
        onClick={() => explicacao && setShowInfo(true)}
        role={explicacao ? 'button' : undefined}
      >
        <Spark
          values={m.spark} color={m.color} labels={m.spark_labels}
          unit={m.unit} decimals={m.decimals} valueDisplays={m.spark_display}
        />
      </div>
      <div className="hh-axis">{m.axis.map((a, i) => <span key={i}>{a}</span>)}</div>
      {showInfo && explicacao && (
        <InfoModal title={m.label} onClose={() => setShowInfo(false)}>
          <p>{explicacao}</p>
        </InfoModal>
      )}
    </div>
  );
}

// Traçado do ECG — linha contínua (não barras), em cima/embaixo da linha de
// base conforme a voltagem. Some sozinho até clicar pra expandir.
export function EcgWave({ values, color }) {
  if (!values || values.length === 0) return null;
  const W = 300, H = 80;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const n = values.length;
  const pontos = values
    .map((v, i) => `${(i / (n - 1)) * W},${H - ((v - min) / range) * H}`)
    .join(' ');

  return (
    <svg className="hh-ecg-wave" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <polyline points={pontos} fill="none" stroke={color || '#22c55e'} strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
